import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Phone, Mail, MapPin, Calendar, Clock, DollarSign, Filter, RefreshCw, Search, ChevronDown, ChevronUp, Bell, BellOff, LogOut, Users, ClipboardList, Star, Edit, Trash2, CheckCircle, Send } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import CustomerManagement from './CustomerManagement';
import ReviewManagement from './ReviewManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REFRESH_INTERVAL = 30000; // 30 seconds

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bookings'); // 'bookings', 'customers', or 'reviews'
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCity, setFilterCity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [lastBookingCount, setLastBookingCount] = useState(0);
  const [lastPaidCount, setLastPaidCount] = useState(0);
  const [newBookingsCount, setNewBookingsCount] = useState(0);
  const [adminUser, setAdminUser] = useState('');
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [isRescheduling, setIsRescheduling] = useState(false);
  const seenBookingIds = useRef(new Set());
  const audioRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // booking id to confirm delete
  const [deleteReason, setDeleteReason] = useState(''); // reason for cancellation
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmingBooking, setConfirmingBooking] = useState(null); // booking being confirmed
  const [isConfirming, setIsConfirming] = useState(false);

  const availableTimes = [
    "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"
  ];

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Delete booking function with reason
  const handleDeleteBooking = async (bookingId) => {
    setIsDeleting(true);
    try {
      const params = deleteReason ? `?reason=${encodeURIComponent(deleteReason)}` : '';
      await axios.delete(`${API}/bookings/${bookingId}${params}`);
      toast.success('Booking deleted and customer notified');
      setDeleteConfirm(null);
      setDeleteReason('');
      fetchBookings(); // Refresh the list
    } catch (error) {
      toast.error('Failed to delete booking');
    } finally {
      setIsDeleting(false);
    }
  }

  // Confirm booking function - sends confirmation email
  const handleConfirmBooking = async (booking) => {
    if (!booking.email) {
      toast.error('Cannot send confirmation - customer has no email');
      return;
    }
    
    setIsConfirming(true);
    try {
      const response = await axios.post(`${API}/admin/bookings/${booking.id}/confirm`);
      if (response.data.success) {
        toast.success(response.data.message);
        setConfirmingBooking(null);
        fetchBookings(); // Refresh to show confirmed status
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send confirmation';
      toast.error(message);
    } finally {
      setIsConfirming(false);
    }
  };
  
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    today: 0
  });

  // Check authentication on mount
  useEffect(() => {
    const authToken = localStorage.getItem('laundry_admin_auth');
    const username = localStorage.getItem('laundry_admin_user');
    
    if (!authToken || !username) {
      toast.error('Please login to access admin dashboard');
      navigate('/admin/login');
      return;
    }
    
    setAdminUser(username);
  }, [navigate]);

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('laundry_admin_auth');
    localStorage.removeItem('laundry_admin_user');
    localStorage.removeItem('laundry_admin_login_time');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    try {
      if (typeof window !== 'undefined' && 'Notification' in window && typeof Notification !== 'undefined') {
        // If already enabled, toggle off (app-level only)
        if (notificationsEnabled) {
          setNotificationsEnabled(false);
          toast.info('Dashboard notifications disabled');
          return;
        }
        
        // Check current permission state
        if (Notification.permission === 'granted') {
          setNotificationsEnabled(true);
          toast.success('Browser notifications enabled!');
          new Notification('🧺 Dashboard Notifications Active', {
            body: 'You will receive alerts for new bookings and payments',
            icon: '/logo192.png'
          });
        } else if (Notification.permission === 'denied') {
          toast.error('Notifications blocked by browser. Please enable in browser settings.');
        } else {
          // Request permission
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            setNotificationsEnabled(true);
            toast.success('Browser notifications enabled!');
            new Notification('🧺 Dashboard Notifications Active', {
              body: 'You will receive alerts for new bookings and payments',
              icon: '/logo192.png'
            });
          } else {
            toast.error('Notification permission denied');
          }
        }
      } else {
        console.log('Notifications not supported in this browser');
        toast.info('Browser notifications not available - dashboard will still show all bookings');
      }
    } catch (error) {
      console.error('Notification error:', error);
      toast.info('Notifications not available - dashboard will still work');
    }
  };

  const fetchBookings = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      
      const response = await axios.get(`${API}/bookings`);
      
      if (response.data.success) {
        const bookingsData = response.data.bookings;
        
        // Calculate stats
        const today = new Date().toISOString().split('T')[0];
        const newStats = {
          total: bookingsData.length,
          pending: bookingsData.filter(b => b.status === 'pending').length,
          paid: bookingsData.filter(b => b.status === 'paid').length,
          today: bookingsData.filter(b => b.createdAt.split('T')[0] === today).length
        };
        
        // Check for new bookings
        if (lastBookingCount > 0 && bookingsData.length > lastBookingCount) {
          const newBookingsNum = bookingsData.length - lastBookingCount;
          setNewBookingsCount(prev => prev + newBookingsNum);
          
          // Find new bookings
          const newBookings = bookingsData.filter(b => !seenBookingIds.current.has(b.id));
          
          newBookings.forEach(booking => {
            if (notificationsEnabled && typeof window !== 'undefined' && 'Notification' in window && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              try {
                const notification = new Notification('🧺 New Booking Received!', {
                  body: `${booking.name} - ${booking.city}\nPickup: ${booking.pickupDate}`,
                  icon: '/logo192.png',
                  badge: '/logo192.png',
                  tag: 'laundry-booking'
                });

                notification.onclick = () => {
                  window.focus();
                  notification.close();
                };
              } catch (e) {
                console.log('Notification failed:', e);
              }
            }
          });
          
          if (!silent) {
            toast.success(`${newBookingsNum} new booking(s) received!`);
          }
        }
        
        // Check for new payments
        if (lastPaidCount > 0 && newStats.paid > lastPaidCount) {
          const newPayments = newStats.paid - lastPaidCount;
          
          if (notificationsEnabled && typeof window !== 'undefined' && 'Notification' in window && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            try {
              const notification = new Notification('💳 Payment Received!', {
                body: `${newPayments} new payment(s) completed`,
                icon: '/logo192.png',
                badge: '/logo192.png',
                tag: 'laundry-booking',
                requireInteraction: true
              });

              // Play sound for paid bookings
              if (audioRef.current) {
                audioRef.current.play().catch(e => console.log('Audio play failed:', e));
              }

              notification.onclick = () => {
                window.focus();
                notification.close();
              };
            } catch (e) {
              console.log('Notification failed:', e);
            }
          }
          
          if (!silent) {
            toast.success(`💰 ${newPayments} new payment(s) received!`, {
              duration: 5000
            });
          }
        }
        
        // Update seen booking IDs
        bookingsData.forEach(b => seenBookingIds.current.add(b.id));
        
        setBookings(bookingsData);
        setStats(newStats);
        setLastBookingCount(bookingsData.length);
        setLastPaidCount(newStats.paid);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      if (!silent) {
        toast.error('Failed to load bookings');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [lastBookingCount, lastPaidCount, notificationsEnabled]);

  // Initial fetch
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      fetchBookings(true); // Silent refresh
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchBookings]);

  // Request notification permission on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && typeof Notification !== 'undefined') {
      if (Notification.permission === 'default') {
        // Auto-request after 2 seconds
        const timer = setTimeout(() => {
          requestNotificationPermission();
        }, 2000);
        return () => clearTimeout(timer);
      } else if (Notification.permission === 'granted') {
        setNotificationsEnabled(true);
      }
    } else {
      console.log('Notifications not supported, dashboard will still function normally');
    }
  }, []);

  // Handle admin reschedule
  const handleAdminReschedule = async (bookingId) => {
    if (!newDate) {
      toast.error('Please select a new date');
      return;
    }

    setIsRescheduling(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/reschedule`, {
        pickupDate: newDate,
        pickupTime: newTime || rescheduleBooking.pickupTime
      });

      toast.success('Booking rescheduled successfully!');
      setRescheduleBooking(null);
      setNewDate('');
      setNewTime('');
      fetchBookings(); // Refresh bookings
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reschedule';
      toast.error(message);
    } finally {
      setIsRescheduling(false);
    }
  };

  // Clear new bookings badge when user views the list
  const clearNewBookingsBadge = () => {
    setNewBookingsCount(0);
  };

  // Filter bookings
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = 
      booking.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.phone.includes(searchTerm) ||
      (booking.email && booking.email.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCity = filterCity === 'all' || booking.city === filterCity;
    const matchesStatus = filterStatus === 'all' || booking.status === filterStatus;
    
    return matchesSearch && matchesCity && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'completed': return 'bg-green-100 text-green-800 border-green-300';
      case 'delivered': return 'bg-green-100 text-green-800 border-green-300';
      case 'ready-for-delivery': return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      case 'in-progress': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'picked-up': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'order-received': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  
  // Format status for display
  const formatStatus = (status) => {
    switch(status) {
      case 'order-received': return 'Order Received';
      case 'picked-up': return 'Picked Up';
      case 'in-progress': return 'In Progress';
      case 'ready-for-delivery': return 'Ready for Delivery';
      case 'delivered': return 'Delivered';
      case 'paid': return 'Paid';
      case 'completed': return 'Completed';
      case 'pending': return 'Pending';
      default: return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown';
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const response = await axios.put(`${API}/bookings/${bookingId}/status`, {
        status: newStatus
      });

      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh bookings to show updated status
        await fetchBookings(true);
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update booking status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50">
      {/* Hidden audio element for payment notifications */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBCt9zPLTgjMGHW7A7+OZSA0PVa/m77BdGAg+mtvyw3QmBSyBzvLZiTYIG2m98OWhURAKTqHh8bllHAU2jdXywn0pBSh4yO/ekDwKE1y26+ulUxELRp3f8sFuJAUpfMzy1IU1Bx1tv/Dlnk0NC1Kq5O+zYhkIP5na88V3KAUsgs7y2Ik1CBtovO7mnlENDlCn4/C0ZBwGOI/X8st7KwUme8rx34xAChVes+rspVMRDEae3/K+bSMFKXzN8tWFNgcdbr/w5qBODQxUr+fvtGQcBz2Y2/PEeCgFLIHO8tmJNwgaaLzu5p5ODQ9Rp+Pws2McBjiP1vHKfCsGJ3vJ8d+MQAoVXrPq7KVUEQxHnt/yvm0kBSl8zfLVhTYHHW6/8OajUQ0MVa7n77RkHAc9mNryw3krBSt/zvHYiTYIG2i78OShURAMUKji8LNjHAY4jtXxx34qBSd5x+/gjkILFFyx6+ymVBIMR53f8r5tJAUpfM3y1oU2Bx1uv/Dmo1ENDFSu5++zYxwHPZjZ8cJ5KgUrf87y2Ik2CBppvO7mnU4ND1Gn4vC0YxwGN47V8cd+KgUnesjv4I5CChRdse3tplURDEed3/K+bSQFKXzN8taFNgcdcL7v46JQDAxUrufvs2McBz2Y2fHCeSoFK3/O8tiJNggaaLvt5Z5ODg9Qp+Lws2McBjiO1vHHfioFJ3rI8N+OQgoTXLHr7KdUEQxGnN/yvm0jBSp8zfLWhjYHHXC+7+OiUA0LVK7n77RkHAg9mNnxwnkqBSp/zvHYiTYIG2m77eSeUA4PUKfi77NjHAY4jtXxx34rBSd6x+/fjkELE1yw6+ymVREMRpzf8r5uIwUqfM3y1oY2Bx5vvu7io08NC1Ot5+60ZBwHPZjY8cN5KgUqf87x2Yk2CBtpvO3knlAPD0+n4u+zYxwHOI7V8Mh+KwUnesfu34xCChNcr+rsp1QRDEad3vLAbSQEKXzN8teGNgceb73t46NQDQ1Sq+XvtGMcBz6Y2PHCeyoFKn/O8dmJNQgbaLvt5Z5PDxBPpt/us2IdBziO1vHHfywFKHnH7d6OQQsUXK/p66dVEQ1Hnd3ywGwkBCp9zfLYhTcHHm+97uOjTw0MUqrl7rZkGwc9l9fzu3ksCSh+zvHaiTQIG2i77eWeTQ0PTqff7rJiHQc4jtXxx38rBSh5x+3fj0ALFFmq5euoVRIMR53e8cFsJQUqfM3y2IU3Bx5tu+7ko08NDFGp4++yYhsHPZfX87t5KwQofcvv2IkzCBtov+3knk0ND06n3u2xYR0GN4/U8Mh+LAUqecbt3o9CChNareXqplQRDEad3vHAbSQFKnzN8tiFNwcfbbrt5KRPDAxRp+Pus2MbBz2W1vK7eSsFKHzL7tiKMwcbaL/s5J5NDQ9Op93ssWIdBjWO1PDHfysEKnnG7N6PQgsUWqzj6aZUEAxHnd3xv2wkBCp8zPHZhTcHH26+7OOkTwwNUqbh77NjGwg8lNXxvHgrBSh9yu3YijMHG2i/6+OeTQ0QT6fc7LBiHQY2jtTwx34rBCl5xuzej0ILFFqs4+mmVBEMRp3d8L9rJAQqfczy2YY3Bx9uvuzjpE8MDVKm4O+xYRsIPJXV8Lx4KgQpfcvt2IozBxpnv+vjn04ND1Cm3Oyvbx0GNo/U8Mh/KgQqeMXs3o9BCxRZrOPoplQRDUac3fC/ayQEKn3L8diGNgcfb7zs46VOCw1Spt/wsGEbBz2V1fC8eCsFKH3L7diJMwcaaL/q4p5NDhBQp9zssGAcBjaP1PDIfikEKnjF694PQgsUW63i6adUEA1GnN3wvmwkBCp8y/HYhjYGH2+87OOlTgsNVKbe8K9iGwc9ltXwvHkqBSh8yuzYiTMHGWi/6+KgTg4QUKfc663hGwY2j9Pwx34qBCl4xerdj0EKFFut4uilVRANR5zd776sZBwEKnvK8NiFNgYfbrrs46VOCQ1WqOHvr2AaBz6W1O+8eSoEKHzK7NiKMwgZZ77q4aFNDRBRp9vrrWEbBTaP0+/HfioEKXjE6N2PQQsUXK7h6KVSEA1Jnd3u" />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-teal-100 mt-1">Laundry 24hr Lavanderia</p>
              <p className="text-teal-100 text-sm mt-1">
                🔄 Auto-refreshing every 30 seconds
                {adminUser && <span className="ml-3">👤 Logged in as: {adminUser}</span>}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => requestNotificationPermission()}
                variant="outline"
                className={`${notificationsEnabled ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-teal-700 text-white hover:bg-teal-800'} border-0 cursor-pointer transition-colors`}
              >
                {notificationsEnabled ? <Bell size={16} className="mr-2" /> : <BellOff size={16} className="mr-2" />}
                {notificationsEnabled ? 'Notifications On' : 'Enable Notifications'}
              </Button>
              <Button
                onClick={() => {
                  fetchBookings();
                  clearNewBookingsBadge();
                }}
                variant="outline"
                className="bg-white text-teal-600 hover:bg-teal-50 border-0 relative"
              >
                <RefreshCw size={16} className="mr-2" />
                Refresh
                {newBookingsCount > 0 && (
                  <Badge className="absolute -top-2 -right-2 bg-red-500 text-white px-2 py-1">
                    {newBookingsCount}
                  </Badge>
                )}
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="bg-white text-teal-600 hover:bg-teal-50 border-0"
              >
                Website
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="bg-white text-red-600 hover:bg-red-50 border-0"
              >
                <LogOut size={16} className="mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-4 mb-8 border-b pb-4">
          <Button
            variant={activeTab === 'bookings' ? 'default' : 'outline'}
            onClick={() => setActiveTab('bookings')}
            className={activeTab === 'bookings' ? 'bg-teal-600 hover:bg-teal-700' : 'border-teal-600 text-teal-600 hover:bg-teal-50'}
          >
            <ClipboardList size={18} className="mr-2" />
            Bookings
            {newBookingsCount > 0 && (
              <Badge className="ml-2 bg-red-500 text-white">{newBookingsCount}</Badge>
            )}
          </Button>
          <Button
            variant={activeTab === 'customers' ? 'default' : 'outline'}
            onClick={() => setActiveTab('customers')}
            className={activeTab === 'customers' ? 'bg-teal-600 hover:bg-teal-700' : 'border-teal-600 text-teal-600 hover:bg-teal-50'}
          >
            <Users size={18} className="mr-2" />
            Customers & Loyalty
          </Button>
          <Button
            variant={activeTab === 'reviews' ? 'default' : 'outline'}
            onClick={() => setActiveTab('reviews')}
            className={activeTab === 'reviews' ? 'bg-teal-600 hover:bg-teal-700' : 'border-teal-600 text-teal-600 hover:bg-teal-50'}
          >
            <Star size={18} className="mr-2" />
            Reviews
          </Button>
        </div>

        {/* Bookings Tab Content */}
        {activeTab === 'bookings' && (
          <>
            {/* Stats Cards */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-teal-600">{stats.total}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Pending</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-yellow-600">{stats.pending}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Paid</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-600">{stats.paid}</div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-gray-600">Today</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-600">{stats.today}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter size={20} />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  <Search size={16} className="inline mr-2" />
                  Search
                </label>
                <Input
                  type="text"
                  placeholder="Name, phone, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-gray-300"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  <MapPin size={16} className="inline mr-2" />
                  City
                </label>
                <Select value={filterCity} onValueChange={setFilterCity}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    <SelectItem value="Lawrenceville">Lawrenceville</SelectItem>
                    <SelectItem value="Lilburn">Lilburn</SelectItem>
                    <SelectItem value="Norcross">Norcross</SelectItem>
                    <SelectItem value="Snellville">Snellville</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  <Package size={16} className="inline mr-2" />
                  Status
                </label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="order-received">Order Received</SelectItem>
                    <SelectItem value="picked-up">Picked Up</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="ready-for-delivery">Ready for Delivery</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bookings List */}
        <Card>
          <CardHeader>
            <CardTitle>All Bookings ({filteredBookings.length})</CardTitle>
            <CardDescription>
              {loading ? 'Loading bookings...' : 'Click on a booking to view full details'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto mb-4 text-teal-600" size={32} />
                <p className="text-gray-600">Loading bookings...</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12">
                <Package className="mx-auto mb-4 text-gray-400" size={48} />
                <p className="text-gray-600">No bookings found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="border-2 rounded-lg p-4 hover:border-teal-300 transition-all cursor-pointer"
                    onClick={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                  >
                    {/* Collapsed View */}
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{booking.name}</h3>
                          <Badge className={`${getStatusColor(booking.status)} border`}>
                            {formatStatus(booking.status)}
                          </Badge>
                          {booking.paymentOption === 'now' && booking.status === 'paid' && (
                            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">
                              💳 Prepaid
                            </Badge>
                          )}
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone size={14} />
                            <span>{booking.phone}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPin size={14} />
                            <span>{booking.city}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar size={14} />
                            <span>{booking.pickupDate}</span>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Clock size={14} />
                            <span>{booking.pickupTime || 'Not specified'}</span>
                          </div>
                        </div>
                        {/* Service Type, Weight, Price Row */}
                        <div className="grid sm:grid-cols-3 gap-2 text-sm mt-2 pt-2 border-t border-gray-100">
                          <div className="flex items-center gap-2">
                            <Package size={14} className="text-teal-600" />
                            <span className="font-medium text-gray-700">Service:</span>
                            <span className="text-teal-700 font-semibold">{booking.packageName || booking.package || 'Standard'}</span>
                          </div>
                          {booking.weight_lbs && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-700">Weight:</span>
                              <span className="text-gray-800 font-semibold">{booking.weight_lbs} lbs</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <DollarSign size={14} className="text-green-600" />
                            <span className="font-medium text-gray-700">Price:</span>
                            <span className="text-green-700 font-semibold">
                              {booking.totalPrice ? `$${parseFloat(booking.totalPrice).toFixed(2)}` : booking.price ? `$${parseFloat(booking.price).toFixed(2)}` : 'TBD'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500 mb-2">
                          {formatDate(booking.createdAt)}
                        </div>
                        {expandedBooking === booking.id ? (
                          <ChevronUp className="text-teal-600" size={24} />
                        ) : (
                          <ChevronDown className="text-gray-400" size={24} />
                        )}
                      </div>
                    </div>

                    {/* Expanded View */}
                    {expandedBooking === booking.id && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        {/* Status Update Section */}
                        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border-2 border-teal-200">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <Package size={18} className="text-teal-600" />
                            Update Order Status
                          </h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateBookingStatus(booking.id, 'order-received');
                              }}
                              className={`${booking.status === 'order-received' || booking.status === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-all`}
                            >
                              📦 Order Received
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateBookingStatus(booking.id, 'picked-up');
                              }}
                              className={`${booking.status === 'picked-up' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-all`}
                            >
                              🚚 Picked Up
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateBookingStatus(booking.id, 'in-progress');
                              }}
                              className={`${booking.status === 'in-progress' ? 'bg-purple-500 hover:bg-purple-600' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-all`}
                            >
                              🧺 In Progress
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateBookingStatus(booking.id, 'ready-for-delivery');
                              }}
                              className={`${booking.status === 'ready-for-delivery' ? 'bg-indigo-500 hover:bg-indigo-600' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-all`}
                            >
                              📤 Ready for Delivery
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateBookingStatus(booking.id, 'delivered');
                              }}
                              className={`${booking.status === 'delivered' ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-all`}
                            >
                              ✅ Delivered
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateBookingStatus(booking.id, 'paid');
                              }}
                              className={`${booking.status === 'paid' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'} transition-all`}
                            >
                              💳 Paid
                            </Button>
                          </div>
                          <p className="text-xs text-gray-600 mt-2">
                            ℹ️ Customer will receive an email notification when status is updated
                          </p>
                        </div>
                        
                        {booking.email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail size={16} className="text-teal-600" />
                            <span className="font-medium">Email:</span>
                            <a href={`mailto:${booking.email}`} className="text-teal-600 hover:underline">
                              {booking.email}
                            </a>
                          </div>
                        )}
                        
                        {booking.address && (
                          <div className="flex items-start gap-2 text-sm">
                            <MapPin size={16} className="text-teal-600 mt-0.5" />
                            <div>
                              <span className="font-medium">Address:</span>
                              <p className="text-gray-600">{booking.address}</p>
                            </div>
                          </div>
                        )}
                        
                        {booking.notes && (
                          <div className="bg-blue-50 p-3 rounded-lg text-sm">
                            <span className="font-medium text-blue-900">Special Notes:</span>
                            <p className="text-blue-800 mt-1">{booking.notes}</p>
                          </div>
                        )}
                        
                        <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t">
                          <div className="text-xs">
                            <span className="text-gray-500">Booking ID:</span>
                            <p className="font-mono text-gray-700">{booking.id}</p>
                          </div>
                          <div className="text-xs">
                            <span className="text-gray-500">Last Updated:</span>
                            <p className="text-gray-700">{formatDate(booking.updatedAt)}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 pt-3">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.location.href = `tel:${booking.phone}`;
                            }}
                            className="bg-teal-600 hover:bg-teal-700"
                          >
                            <Phone size={14} className="mr-2" />
                            Call Customer
                          </Button>
                          {booking.email && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.location.href = `mailto:${booking.email}`;
                              }}
                            >
                              <Mail size={14} className="mr-2" />
                              Send Email
                            </Button>
                          )}
                          {/* Confirm Booking Button - sends confirmation email */}
                          {booking.email && (booking.status === 'pending' || booking.status === 'order-received') && !booking.confirmed && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmingBooking(booking);
                              }}
                            >
                              <CheckCircle size={14} className="mr-2" />
                              Confirm Booking
                            </Button>
                          )}
                          {/* Show confirmed badge if already confirmed */}
                          {booking.confirmed && (
                            <Badge className="bg-green-100 text-green-800 border border-green-300">
                              <CheckCircle size={12} className="mr-1" />
                              Confirmed
                            </Badge>
                          )}
                          {/* Reschedule Button for Admin */}
                          {(booking.status === 'pending' || booking.status === 'order-received') && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-600 border-amber-300 hover:bg-amber-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                setRescheduleBooking(booking);
                                setNewDate('');
                                setNewTime('');
                              }}
                            >
                              <Edit size={14} className="mr-2" />
                              Reschedule
                            </Button>
                          )}
                          
                          {/* Delete Button for Admin */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm(booking);
                              setDeleteReason('');
                            }}
                          >
                            <Trash2 size={14} className="mr-2" />
                            Delete
                          </Button>
                        </div>
                        {/* Show if rescheduled */}
                        {booking.reschedule_count > 0 && (
                          <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                            📅 Rescheduled from {booking.original_pickup_date} {booking.original_pickup_time && `at ${booking.original_pickup_time}`}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}

        {/* Customers Tab Content */}
        {activeTab === 'customers' && (
          <CustomerManagement />
        )}

        {/* Reviews Tab Content */}
        {activeTab === 'reviews' && (
          <ReviewManagement />
        )}
      </div>

      {/* Admin Reschedule Modal */}
      {rescheduleBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="text-amber-600" size={20} />
                Reschedule Booking
              </CardTitle>
              <CardDescription>
                Customer: {rescheduleBooking.name}<br/>
                Current: {rescheduleBooking.pickupDate} at {rescheduleBooking.pickupTime || 'TBD'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Pickup Date *
                </label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  min={getMinDate()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Pickup Time (Optional)
                </label>
                <select
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Keep current time ({rescheduleBooking.pickupTime || 'TBD'})</option>
                  {availableTimes.map((time) => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                ℹ️ <strong>Note:</strong> The customer will see this updated date/time in their account.
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setRescheduleBooking(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleAdminReschedule(rescheduleBooking.id)}
                  disabled={isRescheduling || !newDate}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                >
                  {isRescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal with Reason */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 size={20} />
                Cancel & Delete Booking
              </CardTitle>
              <CardDescription>
                The customer will be notified of the cancellation via email.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-100 rounded-lg p-4">
                <p className="font-semibold">{deleteConfirm.name}</p>
                <p className="text-sm text-gray-600">{deleteConfirm.phone}</p>
                {deleteConfirm.email && (
                  <p className="text-sm text-gray-600">{deleteConfirm.email}</p>
                )}
                <p className="text-sm text-gray-600">
                  Pickup: {deleteConfirm.pickupDate} at {deleteConfirm.pickupTime || 'TBD'}
                </p>
                <p className="text-sm text-gray-600">Status: {deleteConfirm.status}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail size={14} className="inline mr-2" />
                  Cancellation Reason (sent to customer)
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="e.g., Unable to service this area at the requested time, scheduling conflict, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 min-h-[100px]"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {deleteConfirm.email 
                    ? "This message will be included in the cancellation email sent to the customer." 
                    : "⚠️ No email on file - customer won't receive notification."}
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                ⚠️ <strong>Warning:</strong> This action cannot be undone. The booking and any associated payment records will be permanently deleted.
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteConfirm(null);
                    setDeleteReason('');
                  }}
                  className="flex-1"
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDeleteBooking(deleteConfirm.id)}
                  disabled={isDeleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isDeleting ? (
                    <>
                      <RefreshCw size={14} className="mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={14} className="mr-2" />
                      Delete & Notify
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Booking Modal */}
      {confirmingBooking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle size={20} />
                Confirm Booking
              </CardTitle>
              <CardDescription>
                Send a confirmation email to the customer.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-100 rounded-lg p-4">
                <p className="font-semibold">{confirmingBooking.name}</p>
                <p className="text-sm text-gray-600">{confirmingBooking.phone}</p>
                <p className="text-sm text-teal-600 font-medium">{confirmingBooking.email}</p>
                <p className="text-sm text-gray-600">
                  Pickup: {confirmingBooking.pickupDate} at {confirmingBooking.pickupTime || 'TBD'}
                </p>
                <p className="text-sm text-gray-600">City: {confirmingBooking.city}</p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                <Send size={14} className="inline mr-2" />
                <strong>Email will include:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Booking confirmation with pickup details</li>
                  <li>Pickup date, time, and address</li>
                  <li>What to expect (driver arrival, delivery info)</li>
                  <li>Contact information for any questions</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setConfirmingBooking(null)}
                  className="flex-1"
                  disabled={isConfirming}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleConfirmBooking(confirmingBooking)}
                  disabled={isConfirming}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {isConfirming ? (
                    <>
                      <RefreshCw size={14} className="mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={14} className="mr-2" />
                      Send Confirmation
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
